import type { SeededRandom } from "../core/SeededRandom";
import type { MissionGraphData, Room, RoomKind } from "./types";

// Broad routing lanes separate sectors. The plan rotates and reflects per seed.
const SECTORS: Readonly<Record<RoomKind, readonly [number, number]>> = {
  start: [0, 2],
  encounter: [0, 1],
  hub: [0, 0],
  tech: [1, 0],
  toxic: [2, 0],
  dark: [2, 1],
  finale: [2, 2],
  supply: [1, 1],
  secret: [1, 2],
};

export class RoomGenerator {
  public constructor(private readonly random: SeededRandom) {}

  public generate(graph: MissionGraphData): Room[] {
    const turns = this.random.int(0, 3);
    const reflect = this.random.chance(0.5);
    return graph.nodes.map((node): Room => {
      let [column, row] = SECTORS[node.id];
      if (reflect) column = 2 - column;
      for (let turn = 0; turn < turns; turn += 1) [column, row] = [2 - row, column];
      const width = this.random.int(16, 17);
      const height = this.random.int(16, 17);
      const coordinate = (sector: number, size: number): number =>
        sector === 0 ? 2 : sector === 2 ? 62 - size : 24 + this.random.int(0, 17 - size);
      const x = coordinate(column, width);
      const y = coordinate(row, height);
      const shape =
        node.id === "start"
          ? "octagon"
          : node.id === "hub"
            ? "cross"
            : node.id === "toxic"
              ? "cave"
              : node.id === "finale"
                ? "pillared"
                : this.random.pick(["rectangle", "octagon", "cross", "pillared", "cave"] as const);
      return {
        ...node,
        x,
        y,
        width,
        height,
        shape,
        center: { x: x + Math.floor(width / 2), y: y + Math.floor(height / 2) },
      };
    });
  }
}

export function roomsIntersect(first: Room, second: Room, margin = 2): boolean {
  return !(
    first.x + first.width + margin <= second.x ||
    second.x + second.width + margin <= first.x ||
    first.y + first.height + margin <= second.y ||
    second.y + second.height + margin <= first.y
  );
}
